// CRM Phase 3 — Proposals & PDF generation
import React, { useCallback, useEffect, useState } from "react";
import { Plus, FileText, Download, Send, Check, X, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const EMPTY_ITEM = { description: "", qty: 1, unit_price_gbp: 0 };

const STATUS_STYLE = {
  draft: "bg-muted text-muted-foreground",
  sent: "bg-secondary/15 text-secondary",
  accepted: "bg-primary/15 text-primary",
  declined: "bg-destructive/10 text-destructive",
  expired: "bg-muted text-muted-foreground",
};

export default function CrmProposals() {
  const [proposals, setProposals] = useState([]);
  const [deals, setDeals] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [sendDlg, setSendDlg] = useState(null);
  const [form, setForm] = useState({ deal_id: "", title: "", intro: "", items: [ { ...EMPTY_ITEM } ], tax_rate: 0.20, valid_days: 30, footer: "" });

  const load = useCallback(async () => {
    const [p, d] = await Promise.all([
      api.get("/crm/proposals"),
      api.get("/crm/deals"),
    ]);
    setProposals(p.data); setDeals(d.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { ...EMPTY_ITEM }] }));
  const rmItem = (i) => setForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const updItem = (i, patch) => setForm(f => ({ ...f, items: f.items.map((it, idx) => idx === i ? { ...it, ...patch } : it) }));

  const subtotal = form.items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.unit_price_gbp) || 0), 0);
  const tax = subtotal * (Number(form.tax_rate) || 0);
  const total = subtotal + tax;

  const save = async () => {
    if (!form.deal_id || !form.title) { toast.error("Pick a deal and a title"); return; }
    try {
      await api.post("/crm/proposals", form);
      toast.success("Proposal created");
      setDlg(false);
      setForm({ deal_id: "", title: "", intro: "", items: [ { ...EMPTY_ITEM } ], tax_rate: 0.20, valid_days: 30, footer: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const genPdf = async (p) => {
    try {
      const { data } = await api.post(`/crm/proposals/${p.id}/generate-pdf`);
      toast.success("PDF generated");
      window.open(data.download_url, "_blank", "noopener,noreferrer");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "PDF failed"); }
  };

  const send = async () => {
    try {
      await api.post(`/crm/proposals/${sendDlg.id}/send`, { to_email: sendDlg.to_email || null, message: sendDlg.message || null });
      toast.success("Proposal emailed");
      setSendDlg(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
  };

  const accept = async (p) => { await api.post(`/crm/proposals/${p.id}/accept`); load(); toast.success("Accepted"); };
  const decline = async (p) => {
    const reason = window.prompt("Decline reason (optional)");
    await api.post(`/crm/proposals/${p.id}/decline`, { reason: reason || null });
    load(); toast.success("Declined");
  };
  const del = async (id) => { if (!window.confirm("Delete proposal?")) return; await api.delete(`/crm/proposals/${id}`); load(); };

  return (
    <div className="space-y-6" data-testid="crm-proposals-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl">Proposals</h2>
          <p className="text-sm text-muted-foreground mt-1">Generate branded PDF proposals for your deals — with automated email delivery.</p>
        </div>
        <Button onClick={() => setDlg(true)} data-testid="proposal-new-btn"><Plus className="w-4 h-4 mr-1" /> New proposal</Button>
      </div>

      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-muted-foreground">
            <tr>
              <th className="text-left px-4 py-3">Title / Deal</th>
              <th className="text-left px-4 py-3">Total</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Valid until</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {proposals.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-muted-foreground">No proposals yet.</td></tr>}
            {proposals.map(p => {
              const deal = deals.find(d => d.id === p.deal_id);
              return (
                <tr key={p.id} className="border-t border-border" data-testid={`proposal-row-${p.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{p.title}</div>
                    <div className="text-xs text-muted-foreground">{deal?.title || p.deal_id.slice(0, 8)}</div>
                  </td>
                  <td className="px-4 py-3 font-mono">£{p.total_gbp?.toFixed(2)}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] overline px-2 py-0.5 rounded-full ${STATUS_STYLE[p.status] || "bg-muted"}`}>{p.status}</span>
                  </td>
                  <td className="px-4 py-3 text-xs">{p.valid_until ? new Date(p.valid_until).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Button variant="ghost" size="sm" onClick={() => genPdf(p)} data-testid={`proposal-pdf-${p.id}`}><Download className="w-4 h-4" /></Button>
                    {p.status === "draft" && <Button variant="ghost" size="sm" onClick={() => setSendDlg({ id: p.id, to_email: deal?.contact_email || "", message: "" })} data-testid={`proposal-send-${p.id}`}><Send className="w-4 h-4 text-primary" /></Button>}
                    {p.status === "sent" && <>
                      <Button variant="ghost" size="sm" onClick={() => accept(p)} data-testid={`proposal-accept-${p.id}`}><Check className="w-4 h-4 text-primary" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => decline(p)} data-testid={`proposal-decline-${p.id}`}><X className="w-4 h-4 text-destructive" /></Button>
                    </>}
                    <Button variant="ghost" size="sm" onClick={() => del(p.id)}><Trash2 className="w-4 h-4 text-muted-foreground" /></Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New proposal</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Deal</Label>
                <Select value={form.deal_id} onValueChange={v => setForm({ ...form, deal_id: v })}>
                  <SelectTrigger data-testid="proposal-deal-select"><SelectValue placeholder="Pick a deal" /></SelectTrigger>
                  <SelectContent>{deals.map(d => <SelectItem key={d.id} value={d.id}>{d.title}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Title</Label>
                <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Q1 Enterprise plan" data-testid="proposal-title-input" />
              </div>
            </div>
            <div>
              <Label>Intro</Label>
              <Textarea rows={2} value={form.intro} onChange={e => setForm({ ...form, intro: e.target.value })} placeholder="Hi {{name}}, thanks for your interest…" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label>Line items</Label>
                <Button size="sm" variant="outline" onClick={addItem} data-testid="proposal-add-item-btn">Add row</Button>
              </div>
              <div className="mt-2 space-y-2">
                {form.items.map((it, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2" data-testid={`proposal-item-${i}`}>
                    <Input className="col-span-6" value={it.description} onChange={e => updItem(i, { description: e.target.value })} placeholder="Description" />
                    <Input className="col-span-2" type="number" value={it.qty} onChange={e => updItem(i, { qty: parseFloat(e.target.value || 0) })} placeholder="Qty" />
                    <Input className="col-span-3" type="number" step="0.01" value={it.unit_price_gbp} onChange={e => updItem(i, { unit_price_gbp: parseFloat(e.target.value || 0) })} placeholder="Unit £" />
                    {form.items.length > 1 && <Button variant="ghost" size="sm" onClick={() => rmItem(i)} className="col-span-1"><Trash2 className="w-4 h-4 text-destructive" /></Button>}
                  </div>
                ))}
              </div>
              <div className="mt-3 text-right text-sm space-y-0.5">
                <div>Subtotal: <span className="font-mono">£{subtotal.toFixed(2)}</span></div>
                <div>VAT ({(form.tax_rate * 100).toFixed(0)}%): <span className="font-mono">£{tax.toFixed(2)}</span></div>
                <div className="text-lg font-display">Total: <span className="font-semibold">£{total.toFixed(2)}</span></div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>VAT rate</Label><Input type="number" step="0.01" value={form.tax_rate} onChange={e => setForm({ ...form, tax_rate: parseFloat(e.target.value || 0) })} /></div>
              <div><Label>Valid days</Label><Input type="number" value={form.valid_days} onChange={e => setForm({ ...form, valid_days: parseInt(e.target.value || 30, 10) })} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDlg(false)}>Cancel</Button>
            <Button onClick={save} data-testid="proposal-save-btn">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!sendDlg} onOpenChange={(v) => !v && setSendDlg(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Send proposal</DialogTitle></DialogHeader>
          {sendDlg && (
            <div className="space-y-3">
              <div><Label>Recipient email</Label><Input value={sendDlg.to_email} onChange={e => setSendDlg({ ...sendDlg, to_email: e.target.value })} data-testid="proposal-send-to-input" /></div>
              <div><Label>Message (optional)</Label><Textarea rows={4} value={sendDlg.message} onChange={e => setSendDlg({ ...sendDlg, message: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSendDlg(null)}>Cancel</Button>
            <Button onClick={send} data-testid="proposal-send-confirm-btn"><Send className="w-4 h-4 mr-1" /> Send</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
